import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatRelative } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Shield, Plus, Trash2, Save, X, AlertTriangle, RotateCcw, Copy, Search, PowerOff, Power, KeyRound } from "lucide-react";

// Gate 2 · governance-key assignment controls.
// The backend (`backend/rbac_routes.py`) enforces confirm + reason on any
// grant OR revoke of ``governance.keys.manage``. The frontend surfaces the
// same discipline in the UI: a persistent high-blast-radius banner, an
// explicit acknowledgement checkbox, and a validated free-text reason
// (10–500 chars). The backend remains the authoritative enforcement layer;
// the frontend is a UX aid, not the security boundary.
const GOVERNANCE_KEY_SLUG = "governance.keys.manage";
const GK_REASON_MIN = 10;
const GK_REASON_MAX = 500;

// Admin → Roles page. Lets sysadmins view every role, edit its permission
// set, create custom roles, and delete non-system roles. The product owner
// explicitly required this so role permissions are DB-driven, not hard-coded.
export default function AdminRoles() {
  const { user, refreshPermissions, invalidatePermissions } = useAuth();
  const [roles, setRoles] = useState([]);
  const [registry, setRegistry] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  // Local draft of the selected role's permissions (Set for fast toggling)
  const [draft, setDraft] = useState(new Set());
  const [draftName, setDraftName] = useState("");
  const [draftDesc, setDraftDesc] = useState("");
  const [permQuery, setPermQuery] = useState("");
  // Gate 2 · governance-key dialog state. ``gkDialog`` is either null or
  // ``{ intent: 'grant'|'revoke', onConfirm(reason), onCancel() }``.
  // ``gkReason`` holds the operator's captured reason for the current
  // draft-level toggle so ``save()`` can forward it to the backend.
  const [gkDialog, setGkDialog] = useState(null);
  const [gkReason, setGkReason] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        api.get("/admin/roles"),
        api.get("/permissions"),
      ]);
      setRoles(rolesRes.data.roles || []);
      setRegistry(permsRes.data.permissions || []);
      if (!selectedKey && rolesRes.data.roles?.[0]) {
        setSelectedKey(rolesRes.data.roles[0].key);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync the local draft whenever the selected role changes.
  useEffect(() => {
    if (!selectedKey) return;
    const r = roles.find((x) => x.key === selectedKey);
    if (r) {
      setDraft(new Set(r.permissions || []));
      setDraftName(r.name || "");
      setDraftDesc(r.description || "");
      // Gate 2 · reset any pending governance-key reason when selection
      // changes — the reason is scoped to a single role's draft.
      setGkReason(null);
      setGkDialog(null);
    }
  }, [selectedKey, roles]);

  const selected = roles.find((r) => r.key === selectedKey);

  // Group permissions by their "group" attribute for the matrix UI.
  const grouped = useMemo(() => {
    const out = {};
    for (const p of registry) {
      if (!out[p.group]) out[p.group] = [];
      out[p.group].push(p);
    }
    return out;
  }, [registry]);

  // Track if there are unsaved changes vs the server copy.
  const dirty = useMemo(() => {
    if (!selected) return false;
    if (draftName !== (selected.name || "")) return true;
    if (draftDesc !== (selected.description || "")) return true;
    const serverPerms = new Set(selected.permissions || []);
    if (serverPerms.size !== draft.size) return true;
    for (const p of draft) if (!serverPerms.has(p)) return true;
    return false;
  }, [draft, draftName, draftDesc, selected]);

  const toggle = (key) => {
    // Gate 2 · intercept toggles of ``governance.keys.manage``. The
    // operator MUST acknowledge + provide a 10–500 char reason before the
    // draft mutates. Cancel restores the prior toggle state (i.e. no
    // change is applied). The captured reason is retained until save so
    // the API payload can include it. The same reason covers subsequent
    // toggles of the slug within the same draft session; toggling the
    // slug back to its server value clears the pending reason.
    if (key === GOVERNANCE_KEY_SLUG) {
      const serverHas = new Set(selected?.permissions || []).has(key);
      const currentlyOn = draft.has(key);
      const nextOn = !currentlyOn;
      // If the toggle would restore the server-side state, no confirmation
      // is required — the operator is undoing an in-progress edit.
      if (nextOn === serverHas) {
        setDraft((prev) => {
          const next = new Set(prev);
          if (nextOn) next.add(key); else next.delete(key);
          return next;
        });
        setGkReason(null);
        return;
      }
      setGkDialog({
        intent: nextOn ? "grant" : "revoke",
        onConfirm: (reason) => {
          setDraft((prev) => {
            const next = new Set(prev);
            if (nextOn) next.add(key); else next.delete(key);
            return next;
          });
          setGkReason(reason);
          setGkDialog(null);
        },
        onCancel: () => setGkDialog(null),
      });
      return;
    }
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const save = async () => {
    if (!selected) return;
    // Save confirmation — helps avoid accidental writes especially on
    // sensitive roles (Iter 76.12 spec: "Add save confirmation before
    // applying changes"). Compute the diff so the prompt is informative.
    const serverPerms = new Set(selected.permissions || []);
    const added = [...draft].filter((p) => !serverPerms.has(p));
    const removed = [...serverPerms].filter((p) => !draft.has(p));
    const nameChanged = draftName !== (selected.name || "");
    const descChanged = draftDesc !== (selected.description || "");
    const summary = [
      nameChanged && `• Rename to "${draftName}"`,
      descChanged && "• Update description",
      added.length && `• Add ${added.length} permission(s)`,
      removed.length && `• Remove ${removed.length} permission(s)`,
    ].filter(Boolean).join("\n");
    if (summary && !window.confirm(
      `Save changes to "${selected.name}"?\n\n${summary}\n\n` +
      `These changes take effect on each user's next page load.`
    )) return;
    setSaving(true);
    try {
      // Gate 2 · when the diff adds or removes governance.keys.manage the
      // backend requires ``governance_key_confirm`` + ``governance_key_reason``.
      // The operator captured the reason at toggle-time via ``gkDialog``.
      const touchesGK = added.includes(GOVERNANCE_KEY_SLUG) || removed.includes(GOVERNANCE_KEY_SLUG);
      const body = {
        name: draftName,
        description: draftDesc,
        permissions: Array.from(draft),
      };
      if (touchesGK && gkReason) {
        body.governance_key_confirm = true;
        body.governance_key_reason = gkReason;
      }
      await api.patch(`/admin/roles/${selected.key}`, body);
      toast.success(`Saved · ${draftName}`);
      setGkReason(null);
      await load();
      // Sprint 1B-B · US 1.7A — fail closed after self-role permission changes.
      // If the caller just edited THEIR OWN role, the permission set they
      // hold is now stale. Invalidate immediately (fails closed) BEFORE
      // fetching the new set so any render that runs during the refresh
      // cannot rely on the old permissions. `refresh: false` prevents the
      // invalidator from also queueing its own refresh — we do exactly one
      // forced refresh below.
      if (user?.role === selected.key) {
        invalidatePermissions({
          reason: "self_role_permissions_updated",
          refresh: false,
        });
        await refreshPermissions({
          force: true,
          reason: "self_role_permissions_updated",
        });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const clone = async () => {
    if (!selected) return;
    const suggestedName = `${selected.name} (copy)`;
    const name = window.prompt(
      `Clone "${selected.name}" into a new custom role.\n\n` +
      `The new role will start with an identical permission set.\n` +
      `You can rename or adjust it after it's created.\n\n` +
      `Name for the new role:`,
      suggestedName,
    );
    if (!name) return;
    // Gate 2 · a clone that would grant governance.keys.manage on the
    // new role requires the confirmation + reason before we call the API.
    const sourceHasGK = (selected.permissions || []).includes(GOVERNANCE_KEY_SLUG);
    const doClone = async (extra) => {
      try {
        const { data } = await api.post(`/admin/roles/${selected.key}/clone`, { name, ...extra });
        toast.success(`Created · ${data.name}`);
        await load();
        setSelectedKey(data.key);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    };
    if (sourceHasGK) {
      setGkDialog({
        intent: "grant",
        onConfirm: async (reason) => {
          setGkDialog(null);
          await doClone({
            governance_key_confirm: true,
            governance_key_reason: reason,
          });
        },
        onCancel: () => setGkDialog(null),
      });
      return;
    }
    await doClone({});
  };

  const toggleActive = async () => {
    if (!selected || selected.system) return;
    const nextActive = !(selected.active !== false);
    const label = nextActive ? "Reactivate" : "Deactivate";
    if (!window.confirm(
      `${label} "${selected.name}"?\n\n` +
      (nextActive
        ? "The role will be assignable again."
        : "The role will remain in the registry but can no longer be assigned to new users.")
    )) return;
    try {
      await api.patch(`/admin/roles/${selected.key}`, { active: nextActive });
      toast.success(`${label}d · ${selected.name}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async () => {
    if (!selected || selected.system) return;
    if (!window.confirm(`Delete role "${selected.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/roles/${selected.key}`);
      toast.success("Role deleted");
      setSelectedKey(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resetToDefaults = async () => {
    if (!selected || !selected.system) return;
    const msg = (
      `Reset "${selected.name}" to seeded defaults?\n\n` +
      `This restores the permission set shipped with HarborWell Connect ` +
      `for this role. Any custom additions or removals will be lost.`
    );
    if (!window.confirm(msg)) return;
    // Gate 2 · if the current live permissions or the seeded defaults
    // contain governance.keys.manage AND resetting would toggle it,
    // capture confirm + reason before we call the API.
    const liveHasGK = (selected.permissions || []).includes(GOVERNANCE_KEY_SLUG);
    const defaultHasGK = Array.isArray(selected.default_permissions)
      && selected.default_permissions.includes(GOVERNANCE_KEY_SLUG);
    const resetChangesGK = liveHasGK !== defaultHasGK;
    const doReset = async (extra) => {
      try {
        const { data } = await api.post(`/admin/roles/${selected.key}/reset-to-defaults`, extra);
        toast.success(`Reset · ${data.name || selected.name}`);
        await load();
        // Sprint 1B-B · US 1.7A — same fail-closed sequence as `save` above.
        if (user?.role === selected.key) {
          invalidatePermissions({
            reason: "self_role_reset",
            refresh: false,
          });
          await refreshPermissions({
            force: true,
            reason: "self_role_reset",
          });
        }
      } catch (e) {
        toast.error(formatApiError(e));
      }
    };
    if (resetChangesGK) {
      setGkDialog({
        intent: liveHasGK ? "revoke" : "grant",
        onConfirm: async (reason) => {
          setGkDialog(null);
          await doReset({
            governance_key_confirm: true,
            governance_key_reason: reason,
          });
        },
        onCancel: () => setGkDialog(null),
      });
      return;
    }
    await doReset({});
  };

  if (loading) {
    return <div className="text-center py-12 text-[#1E3A8A]">Loading roles…</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-roles-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="hw-eyebrow mb-2">Access control</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">
            Roles &amp; permissions
          </h1>
          <p className="text-[#334155] mt-2 max-w-2xl">
            Define what each role can do across HarborWell Connect. Changes
            take effect on each user&apos;s next page load. System roles
            cannot be deleted, but their permissions can be edited.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="admin-roles-create-btn"
          className="px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium inline-flex items-center gap-2 hover:bg-[#172554]"
        >
          <Plus className="w-4 h-4" /> Create role
        </button>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-6">
        {/* Role list */}
        <aside className="bg-white border border-[#E5E5E0] rounded-2xl p-3" data-testid="admin-roles-list">
          <ul className="space-y-1">
            {roles.map((r) => {
              const active = r.key === selectedKey;
              return (
                <li key={r.key}>
                  <button
                    type="button"
                    onClick={() => setSelectedKey(r.key)}
                    data-testid={`admin-role-${r.key}`}
                    className={`w-full text-left px-3 py-2.5 rounded-xl transition-colors ${
                      active ? "bg-[#E8ECE7]" : "hover:bg-[#F9F8F6]"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-[#0F172A] truncate">
                        {r.name}
                      </span>
                      {r.system && (
                        <span
                          className="text-[9px] uppercase tracking-wider text-[#1E3A8A] bg-[#E0E7FF] px-1.5 py-0.5 rounded-full"
                          title="System role — cannot be deleted"
                        >
                          System
                        </span>
                      )}
                      {r.active === false && (
                        <span
                          data-testid={`admin-role-inactive-badge-${r.key}`}
                          className="text-[9px] uppercase tracking-wider text-[#7C2D12] bg-[#FED7AA] px-1.5 py-0.5 rounded-full"
                          title="Inactive — new users cannot be assigned this role"
                        >
                          Inactive
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#1E3A8A] mt-0.5">
                      {r.permissions?.length || 0} permission
                      {(r.permissions?.length || 0) === 1 ? "" : "s"}
                    </div>
                    {r.updated_by_name && r.updated_at && (
                      <div
                        className="text-[10px] text-[#334155] mt-1 truncate"
                        data-testid={`admin-role-last-modified-${r.key}`}
                        title={`Last updated ${new Date(r.updated_at).toLocaleString()}`}
                      >
                        Updated {formatRelative(r.updated_at)} · {r.updated_by_name}
                      </div>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Editor */}
        {selected ? (
          <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6 space-y-6" data-testid="admin-roles-editor">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[260px] space-y-3">
                <div>
                  <label className="block text-xs font-medium text-[#334155] mb-1.5">
                    Display name
                  </label>
                  <input
                    type="text"
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    data-testid="admin-role-name-input"
                    className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm"
                  />
                  <div className="text-[10px] font-mono text-[#1E3A8A] mt-1">
                    key: {selected.key}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#334155] mb-1.5">
                    Description
                  </label>
                  <textarea
                    rows={2}
                    value={draftDesc}
                    onChange={(e) => setDraftDesc(e.target.value)}
                    data-testid="admin-role-desc-input"
                    className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={save}
                  disabled={!dirty || saving}
                  data-testid="admin-role-save-btn"
                  className="px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-40 hover:bg-[#172554]"
                >
                  <Save className="w-4 h-4" />
                  {saving ? "Saving…" : "Save changes"}
                </button>
                <button
                  type="button"
                  onClick={clone}
                  data-testid="admin-role-clone-btn"
                  title="Create a new custom role starting from this role's permissions"
                  className="px-3 py-2 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] text-sm font-medium inline-flex items-center gap-2 hover:bg-[#F9F8F6]"
                >
                  <Copy className="w-4 h-4" />
                  Clone
                </button>
                {!selected.system && (
                  <button
                    type="button"
                    onClick={toggleActive}
                    data-testid="admin-role-active-toggle"
                    title={selected.active !== false ? "Deactivate this custom role" : "Reactivate this custom role"}
                    className="px-3 py-2 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] text-sm font-medium inline-flex items-center gap-2 hover:bg-[#F9F8F6]"
                  >
                    {selected.active !== false
                      ? (<><PowerOff className="w-4 h-4" />Deactivate</>)
                      : (<><Power className="w-4 h-4" />Reactivate</>)}
                  </button>
                )}
                {!selected.system && (
                  <button
                    type="button"
                    onClick={remove}
                    data-testid="admin-role-delete-btn"
                    className="px-3 py-2 rounded-full bg-white border border-[#FCA5A5] text-[#991B1B] text-sm font-medium inline-flex items-center gap-2 hover:bg-[#FEE2E2]"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                )}
                {selected.system && selected.key !== "admin" && (
                  <button
                    type="button"
                    onClick={resetToDefaults}
                    data-testid="admin-role-reset-btn"
                    title="Restore this role's permissions to the seeded defaults"
                    className="px-3 py-2 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] text-sm font-medium inline-flex items-center gap-2 hover:bg-[#F9F8F6]"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Reset to defaults
                  </button>
                )}
              </div>
            </div>

            {selected.key === "admin" && (
              <div className="bg-[#FEF3C7] border border-[#FCD34D] rounded-xl p-3 text-xs text-[#92400E] inline-flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  The <strong>System Administrator</strong> role has implicit access to every permission, including any future ones. The checklist below is shown for completeness but cannot be reduced — admins always retain full control.
                </div>
              </div>
            )}

            {/* Gate 2 · persistent high-blast-radius banner. Visible whenever
                the current draft OR the server copy holds governance.keys.manage,
                so operators can never toggle the slug without seeing the
                warning first. */}
            {(draft.has(GOVERNANCE_KEY_SLUG) || (selected.permissions || []).includes(GOVERNANCE_KEY_SLUG)) && (
              <div
                data-testid="admin-role-governance-key-banner"
                className="bg-[#FEE2E2] border border-[#FCA5A5] rounded-xl p-3 text-xs text-[#7F1D1D] flex items-start gap-2"
              >
                <KeyRound className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-semibold text-[#7F1D1D] mb-0.5">
                    Governance-key permission (high blast radius)
                  </div>
                  <div>
                    <span className="font-mono">{GOVERNANCE_KEY_SLUG}</span> unlocks
                    signing-key rotation for the entire platform. Every grant
                    or revoke requires explicit acknowledgement and a written
                    reason (10–500 characters). The audit trail records the
                    operator, timestamp, and reason for compliance review.
                  </div>
                  {gkReason && (
                    <div
                      data-testid="admin-role-governance-key-pending-reason"
                      className="mt-1.5 rounded-md bg-white/70 border border-[#FCA5A5] px-2 py-1 text-[#7F1D1D]"
                    >
                      <span className="font-semibold">Pending reason (will be saved):</span> {gkReason}
                    </div>
                  )}
                </div>
              </div>
            )}

            <DiffFromDefaultsPanel
              role={selected}
              draft={draft}
              registry={registry}
            />

            <div className="flex items-center gap-2 border border-[#E5E5E0] rounded-full px-3 py-2 bg-white max-w-md"
                 data-testid="admin-roles-permission-search-wrap">
              <Search className="w-3.5 h-3.5 text-[#64748B]" />
              <input
                type="text"
                value={permQuery}
                onChange={(e) => setPermQuery(e.target.value)}
                placeholder="Search permissions by name, key, or module…"
                data-testid="admin-roles-permission-search"
                className="flex-1 bg-transparent border-none text-sm focus:outline-none placeholder:text-[#94A3B8]"
              />
              {permQuery && (
                <button
                  type="button"
                  onClick={() => setPermQuery("")}
                  data-testid="admin-roles-permission-search-clear"
                  className="text-[#64748B] hover:text-[#1E3A8A]"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <div className="space-y-5" data-testid="admin-role-permission-matrix">
              {Object.entries(grouped).map(([group, allPerms]) => {
                const q = permQuery.trim().toLowerCase();
                const perms = q
                  ? allPerms.filter((p) =>
                      (p.label || "").toLowerCase().includes(q) ||
                      (p.key || "").toLowerCase().includes(q) ||
                      (p.group || "").toLowerCase().includes(q) ||
                      (p.description || "").toLowerCase().includes(q))
                  : allPerms;
                if (perms.length === 0) return null;
                return (
                <div key={group}>
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="w-3.5 h-3.5 text-[#1E3A8A]" />
                    <h3 className="font-display text-sm font-semibold text-[#0F172A]">
                      {group}
                    </h3>
                    <span className="text-[10px] text-[#1E3A8A]">
                      ({allPerms.filter((p) => draft.has(p.key)).length} / {allPerms.length})
                    </span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-1.5">
                    {perms.map((p) => {
                      const checked = draft.has(p.key);
                      return (
                        <label
                          key={p.key}
                          data-testid={`admin-role-perm-${p.key}`}
                          className={`flex items-start gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                            checked
                              ? "bg-[#E8ECE7] border-[#7E937D]"
                              : "bg-white border-[#E5E5E0] hover:border-[#1E3A8A]/30"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(p.key)}
                            className="mt-0.5"
                          />
                          <div className="text-xs flex-1">
                            <div className="font-medium text-[#0F172A]">{p.label}</div>
                            <div className="font-mono text-[10px] text-[#1E3A8A] mt-0.5">
                              {p.key}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="bg-white border border-[#E5E5E0] rounded-2xl p-8 text-center">
            <p className="text-sm text-[#334155]">Select a role from the left to start editing.</p>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateRoleDialog
          registry={registry}
          onClose={() => setShowCreate(false)}
          onCreated={async (key) => {
            setShowCreate(false);
            await load();
            setSelectedKey(key);
          }}
        />
      )}

      {gkDialog && (
        <GovernanceKeyConfirmDialog
          intent={gkDialog.intent}
          onConfirm={gkDialog.onConfirm}
          onCancel={gkDialog.onCancel}
        />
      )}
    </div>
  );
}

function CreateRoleDialog({ registry, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [perms, setPerms] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  // Gate 2 · governance-key confirmation captured at toggle-time on the
  // create dialog. Same discipline as the main editor.
  const [gkReason, setGkReason] = useState(null);
  const [gkDialog, setGkDialog] = useState(null);

  const grouped = useMemo(() => {
    const out = {};
    for (const p of registry) {
      if (!out[p.group]) out[p.group] = [];
      out[p.group].push(p);
    }
    return out;
  }, [registry]);

  const toggle = (k) => {
    if (k === GOVERNANCE_KEY_SLUG) {
      const currentlyOn = perms.has(k);
      const nextOn = !currentlyOn;
      if (!nextOn) {
        // Toggling off in create-dialog context: drop the pending reason too.
        setPerms((prev) => {
          const next = new Set(prev);
          next.delete(k);
          return next;
        });
        setGkReason(null);
        return;
      }
      setGkDialog({
        intent: "grant",
        onConfirm: (reason) => {
          setPerms((prev) => {
            const next = new Set(prev);
            next.add(k);
            return next;
          });
          setGkReason(reason);
          setGkDialog(null);
        },
        onCancel: () => setGkDialog(null),
      });
      return;
    }
    setPerms((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Please give the role a name");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        permissions: Array.from(perms),
      };
      if (perms.has(GOVERNANCE_KEY_SLUG) && gkReason) {
        body.governance_key_confirm = true;
        body.governance_key_reason = gkReason;
      }
      const { data } = await api.post("/admin/roles", body);
      toast.success(`Role created · ${data.name}`);
      onCreated(data.key);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="admin-roles-create-dialog">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[#E5E5E0] flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">Create a custom role</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[#F9F8F6]" data-testid="admin-roles-create-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5">Role name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="admin-roles-create-name"
              placeholder="e.g. Partner Agency Reviewer"
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5">Description (optional)</label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="admin-roles-create-desc"
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm"
            />
          </div>
          <div className="text-xs text-[#1E3A8A]">Pick the permissions this role should have:</div>
          <div className="space-y-4">
            {Object.entries(grouped).map(([group, items]) => (
              <div key={group}>
                <div className="text-xs font-semibold text-[#0F172A] mb-1.5">{group}</div>
                <div className="grid sm:grid-cols-2 gap-1.5">
                  {items.map((p) => {
                    const on = perms.has(p.key);
                    return (
                      <label
                        key={p.key}
                        data-testid={`admin-roles-create-perm-${p.key}`}
                        className={`flex items-start gap-2 px-3 py-2 rounded-lg border cursor-pointer ${
                          on ? "bg-[#E8ECE7] border-[#7E937D]" : "bg-white border-[#E5E5E0]"
                        }`}
                      >
                        <input type="checkbox" checked={on} onChange={() => toggle(p.key)} className="mt-0.5" />
                        <span className="text-xs flex-1">{p.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#E5E5E0] bg-[#F9F8F6] flex items-center justify-end gap-2 rounded-b-2xl sticky bottom-0">
          <button onClick={onClose} disabled={submitting} data-testid="admin-roles-create-cancel" className="px-4 py-2 rounded-full text-xs font-medium border border-[#E5E5E0] bg-white text-[#334155]">Cancel</button>
          <button onClick={submit} disabled={submitting} data-testid="admin-roles-create-confirm" className="px-5 py-2 rounded-full text-xs font-semibold bg-[#1E3A8A] text-white disabled:opacity-40">
            {submitting ? "Creating…" : "Create role"}
          </button>
        </div>
      </div>
      {gkDialog && (
        <GovernanceKeyConfirmDialog
          intent={gkDialog.intent}
          onConfirm={gkDialog.onConfirm}
          onCancel={gkDialog.onCancel}
        />
      )}
    </div>
  );
}


function DiffFromDefaultsPanel({ role, draft, registry }) {
  const defaults = role?.default_permissions;
  if (!Array.isArray(defaults)) return null; // Not a system role
  const labelByKey = Object.fromEntries((registry || []).map((p) => [p.key, p.label || p.key]));
  const defaultsSet = new Set(defaults);
  const draftArr = Array.from(draft || []);
  const added = draftArr.filter((k) => !defaultsSet.has(k)).sort();
  const removed = defaults.filter((k) => !draft.has(k)).sort();
  if (added.length === 0 && removed.length === 0) {
    return (
      <div
        data-testid="admin-role-diff-panel"
        className="rounded-xl border border-[#E5E5E0] bg-[#F9F8F6] px-3 py-2 text-xs text-[#334155] inline-flex items-center gap-2"
      >
        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#DCFCE7] text-[#166534]">
          In sync
        </span>
        This role currently matches the seeded defaults.
      </div>
    );
  }
  return (
    <div
      data-testid="admin-role-diff-panel"
      className="rounded-xl border border-[#E5E5E0] bg-white p-3 space-y-2"
    >
      <div className="flex items-center gap-2 text-xs text-[#334155]">
        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E]">
          Diff from defaults
        </span>
        <span>
          {added.length} added · {removed.length} removed
        </span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div data-testid="admin-role-diff-added">
          <div className="text-[10px] uppercase tracking-wider text-[#166534] mb-1">
            + Added by admin
          </div>
          {added.length === 0 ? (
            <div className="text-[11px] text-[#94A3B8]">None</div>
          ) : (
            <ul className="space-y-1">
              {added.map((k) => (
                <li
                  key={k}
                  className="text-[11px] px-2 py-1 rounded-lg bg-[#DCFCE7] text-[#166534] flex items-baseline gap-1.5"
                >
                  <span className="font-mono opacity-60">+</span>
                  <span className="truncate" title={k}>{labelByKey[k] || k}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div data-testid="admin-role-diff-removed">
          <div className="text-[10px] uppercase tracking-wider text-[#991B1B] mb-1">
            – Removed by admin
          </div>
          {removed.length === 0 ? (
            <div className="text-[11px] text-[#94A3B8]">None</div>
          ) : (
            <ul className="space-y-1">
              {removed.map((k) => (
                <li
                  key={k}
                  className="text-[11px] px-2 py-1 rounded-lg bg-[#FEE2E2] text-[#991B1B] flex items-baseline gap-1.5"
                >
                  <span className="font-mono opacity-60">−</span>
                  <span className="truncate" title={k}>{labelByKey[k] || k}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Gate 2 · Governance-key confirmation dialog.
//
// Mirrors the backend rules enforced in ``backend/rbac_routes.py``:
//   C1 — explicit ``governance_key_confirm: true`` acknowledgement
//   C2 — free-text reason
//   C3 — reason length 10–500 chars
//
// This component is the ONLY UX surface that captures the reason. The
// backend remains the authoritative enforcement layer — even if the UI
// is bypassed (curl, script), the backend rejects the request with a
// clear HTTP 400 error signature.
// ─────────────────────────────────────────────────────────────────────
function GovernanceKeyConfirmDialog({ intent, onConfirm, onCancel }) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [reason, setReason] = useState("");
  const trimmed = reason.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < GK_REASON_MIN;
  const tooLong = trimmed.length > GK_REASON_MAX;
  const reasonValid = trimmed.length >= GK_REASON_MIN && trimmed.length <= GK_REASON_MAX;
  const canConfirm = acknowledged && reasonValid;
  const action = intent === "grant" ? "Grant" : "Revoke";
  const actionVerb = intent === "grant" ? "grant" : "revoke";

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
      data-testid="governance-key-confirm-dialog"
    >
      <div className="bg-white rounded-2xl max-w-lg w-full">
        <div className="px-6 py-4 border-b border-[#E5E5E0] flex items-start gap-3">
          <div className="rounded-full bg-[#FEE2E2] p-2 flex-shrink-0">
            <KeyRound className="w-5 h-5 text-[#991B1B]" />
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold text-[#0F172A]">
              {action} governance-key permission
            </h3>
            <p className="text-xs text-[#334155] mt-1">
              You are about to {actionVerb}{" "}
              <span className="font-mono">{GOVERNANCE_KEY_SLUG}</span>.
              This is a high-blast-radius change — it unlocks signing-key
              rotation for the entire platform. Please acknowledge and
              record your reason below.
            </p>
          </div>
        </div>

        <div className="px-6 py-4 space-y-4">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              data-testid="governance-key-ack-checkbox"
              className="mt-0.5"
            />
            <span className="text-xs text-[#334155]">
              I acknowledge that this action changes control over the
              platform&apos;s governance key and will be recorded in the
              audit trail with my identity, timestamp, and the reason
              below.
            </span>
          </label>

          <div>
            <label className="block text-xs font-medium text-[#334155] mb-1.5">
              Reason (required — {GK_REASON_MIN}–{GK_REASON_MAX} characters)
            </label>
            <textarea
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, GK_REASON_MAX))}
              maxLength={GK_REASON_MAX}
              data-testid="governance-key-reason-input"
              placeholder={`e.g. Rotating key custodian for FY26 audit rehearsal (change ticket CHG-1234).`}
              className={`w-full px-3 py-2 rounded-lg border bg-white text-sm ${
                tooShort || tooLong ? "border-[#FCA5A5]" : "border-[#E5E5E0]"
              }`}
            />
            <div className="flex items-center justify-between mt-1">
              <div
                data-testid="governance-key-reason-error"
                className={`text-[11px] ${tooShort ? "text-[#991B1B]" : "text-[#64748B]"}`}
              >
                {tooShort
                  ? `Reason too short — needs at least ${GK_REASON_MIN} characters.`
                  : `Records why the platform's signing-key control is changing.`}
              </div>
              <div className="text-[11px] text-[#64748B] font-mono" data-testid="governance-key-reason-count">
                {trimmed.length}/{GK_REASON_MAX}
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#E5E5E0] bg-[#F9F8F6] flex items-center justify-end gap-2 rounded-b-2xl">
          <button
            type="button"
            onClick={onCancel}
            data-testid="governance-key-cancel-btn"
            className="px-4 py-2 rounded-full text-xs font-medium border border-[#E5E5E0] bg-white text-[#334155] hover:bg-[#F1F5F9]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(trimmed)}
            disabled={!canConfirm}
            data-testid="governance-key-confirm-btn"
            className="px-5 py-2 rounded-full text-xs font-semibold bg-[#991B1B] text-white disabled:opacity-40 hover:bg-[#7F1D1D]"
          >
            {action} permission
          </button>
        </div>
      </div>
    </div>
  );
}
